import { create } from 'zustand';
import { chatApi } from '../services/api-services';

export interface ChatMessage {
  type: string;
  content: string;
  thinking?: string;
  timestamp?: string;
  tokenUsage?: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
    latency: number;
  };
}

interface ChatSession {
  messages: ChatMessage[];
  sessionId: string;
  loading: boolean;
  statusText: string;
  currentTaskId: string;
  ws: WebSocket | null;
}

const defaultSession = (): ChatSession => ({
  messages: [],
  sessionId: '',
  loading: false,
  statusText: '正在思考...',
  currentTaskId: '',
  ws: null,
});

interface ChatStore {
  sessions: Record<string, ChatSession>;
  getSession: (key: string) => ChatSession;
  updateSession: (key: string, patch: Partial<Omit<ChatSession, 'ws'>>) => void;
  appendMessage: (key: string, msg: ChatMessage) => void;
  updateLastAiMessage: (key: string, delta: string) => void;
  updateLastAiThinking: (key: string, delta: string) => void;
  setWs: (key: string, ws: WebSocket | null) => void;
  clearSession: (key: string) => void;
  setSessionId: (key: string, sessionId: string) => void;
}

const getRuntimeStatusText = (content: string) => {
  const text = content.replace(/^数字人[：:]\s*/, '').trim();
  if (!text || text.length > 80) return '';
  if (/^(正在)?思考[。.\s]*$/.test(text)) return '正在思考...';
  if (/^(正在)?(调用|执行)(脚本|命令|工具)/.test(text)) return text.startsWith('正在') ? text : `正在${text}`;
  return '';
};

export const useChatStore = create<ChatStore>((set, get) => ({
  sessions: {},

  getSession: (key) => get().sessions[key] ?? defaultSession(),

  updateSession: (key, patch) =>
    set((s) => ({
      sessions: {
        ...s.sessions,
        [key]: { ...(s.sessions[key] ?? defaultSession()), ...patch },
      },
    })),

  appendMessage: (key, msg) =>
    set((s) => {
      const sess = s.sessions[key] ?? defaultSession();
      return {
        sessions: {
          ...s.sessions,
          [key]: { ...sess, messages: [...sess.messages, msg] },
        },
      };
    }),

  updateLastAiMessage: (key, delta) =>
    set((s) => {
      const sess = s.sessions[key] ?? defaultSession();
      const msgs = [...sess.messages];
      const lastIdx = msgs.length - 1;
      if (lastIdx >= 0 && msgs[lastIdx].type === 'ai') {
        msgs[lastIdx] = { ...msgs[lastIdx], content: msgs[lastIdx].content + delta };
      }
      return { sessions: { ...s.sessions, [key]: { ...sess, messages: msgs } } };
    }),

  updateLastAiThinking: (key, delta) =>
    set((s) => {
      const sess = s.sessions[key] ?? defaultSession();
      const msgs = [...sess.messages];
      const lastIdx = msgs.length - 1;
      if (lastIdx >= 0 && msgs[lastIdx].type === 'ai') {
        msgs[lastIdx] = { ...msgs[lastIdx], thinking: (msgs[lastIdx].thinking ?? '') + delta };
      }
      return { sessions: { ...s.sessions, [key]: { ...sess, messages: msgs } } };
    }),

  setWs: (key, ws) =>
    set((s) => ({
      sessions: {
        ...s.sessions,
        [key]: { ...(s.sessions[key] ?? defaultSession()), ws },
      },
    })),

  clearSession: (key) =>
    set((s) => {
      const sess = s.sessions[key];
      if (sess?.ws) { sess.ws.close(); }
      return {
        sessions: {
          ...s.sessions,
          [key]: { ...defaultSession() },
        },
      };
    }),

  setSessionId: (key, sessionId) =>
    set((s) => ({
      sessions: {
        ...s.sessions,
        [key]: { ...(s.sessions[key] ?? defaultSession()), sessionId },
      },
    })),
}));

/** Connect WebSocket for a given storeKey; messages are written directly to store */
export function connectWs(storeKey: string, onOpen?: () => void) {
  const token = localStorage.getItem('token');
  if (!token) return;

  const store = useChatStore.getState();
  const existing = store.sessions[storeKey]?.ws;

  // Reuse already-open connection
  if (existing && existing.readyState === WebSocket.OPEN) {
    onOpen?.();
    return;
  }

  // Reuse connecting WS — just queue the callback
  if (existing && existing.readyState === WebSocket.CONNECTING) {
    const prevOnOpen = existing.onopen as ((e: Event) => void) | null;
    existing.onopen = (e: Event) => {
      prevOnOpen?.(e);
      onOpen?.();
    };
    return;
  }

  const url = chatApi.getWsUrl(token);
  const ws = new WebSocket(url);
  useChatStore.getState().setWs(storeKey, ws);

  ws.onopen = () => { onOpen?.(); };

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      const { updateSession, appendMessage, updateLastAiMessage, setSessionId, getSession } = useChatStore.getState();

      if (data.type === 'task_started') {
        if (data.task_id) updateSession(storeKey, { currentTaskId: data.task_id });
        appendMessage(storeKey, { type: 'ai', content: '', timestamp: new Date().toLocaleTimeString() });
      } else if (data.type === 'output') {
        const chunk = data.content ?? '';
        const runtimeStatusText = getRuntimeStatusText(chunk);
        if (runtimeStatusText) {
          updateSession(storeKey, { statusText: runtimeStatusText });
        } else {
          updateLastAiMessage(storeKey, chunk);
        }
      } else if (data.type === 'replace_content') {
        const sess = getSession(storeKey);
        const msgs = [...sess.messages];
        const lastIdx = msgs.length - 1;
        if (lastIdx >= 0 && msgs[lastIdx].type === 'ai') {
          msgs[lastIdx] = { ...msgs[lastIdx], content: data.content ?? '' };
          updateSession(storeKey, { messages: msgs });
        }
      } else if (data.type === 'thinking') {
        if (data.content) {
          const { updateLastAiThinking } = useChatStore.getState();
          updateLastAiThinking(storeKey, data.content);
        }
        updateSession(storeKey, { statusText: '正在思考...' });
      } else if (data.type === 'tool_call') {
        updateSession(storeKey, {
          statusText: data.content ? `正在执行: ${data.content.slice(0, 40)}` : '正在思考...',
        });
      } else if (data.type === 'token_usage') {
        // 存储 token 使用信息到最后一条 AI 消息
        const sess = getSession(storeKey);
        const msgs = [...sess.messages];
        const lastIdx = msgs.length - 1;
        if (lastIdx >= 0 && msgs[lastIdx].type === 'ai') {
          msgs[lastIdx] = { ...msgs[lastIdx], tokenUsage: data.data };
          updateSession(storeKey, { messages: msgs });
        }
      } else if (data.type === 'heartbeat') {
        updateSession(storeKey, { statusText: '正在处理中...' });
      } else if (data.type === 'done' || data.type === 'stopped') {
        updateSession(storeKey, { loading: false, currentTaskId: '', statusText: '正在思考...' });
        updateSession(storeKey, { loading: false, currentTaskId: '', statusText: '正在思考...' });
      } else if (data.type === 'info') {
        // 系统提示消息（如会话过期提示），以 info 气泡显示
        appendMessage(storeKey, { type: 'info', content: data.content || '', timestamp: new Date().toLocaleTimeString() });
      } else if (data.type === 'session_expired') {
        appendMessage(storeKey, { type: 'session_expired', content: '', timestamp: new Date().toLocaleTimeString() });
        updateSession(storeKey, { loading: false, currentTaskId: '', statusText: '正在思考...' });
      } else if (data.type === 'error') {
        const sess = getSession(storeKey);
        const msgs = [...sess.messages];
        const lastIdx = msgs.length - 1;
        if (lastIdx >= 0 && msgs[lastIdx].type === 'ai' && !msgs[lastIdx].content) {
          msgs[lastIdx] = { ...msgs[lastIdx], content: data.content || '请求出错', type: 'error' };
          updateSession(storeKey, { messages: msgs, loading: false, currentTaskId: '', statusText: '正在思考...' });
        } else {
          appendMessage(storeKey, { type: 'error', content: data.content || '请求出错', timestamp: new Date().toLocaleTimeString() });
          updateSession(storeKey, { loading: false, currentTaskId: '', statusText: '正在思考...' });
        }
      }

      if (data.session_id && !getSession(storeKey).sessionId) {
        setSessionId(storeKey, data.session_id);
      }
    } catch (e) {
      console.error('WS parse error:', e);
    }
  };

  ws.onerror = () => {
    useChatStore.getState().updateSession(storeKey, { loading: false });
  };

  ws.onclose = () => {
    const state = useChatStore.getState();
    state.setWs(storeKey, null);
    if (state.sessions[storeKey]?.loading) {
      state.updateSession(storeKey, { loading: false });
    }
  };
}
